import { arrayUnion, doc, increment, onSnapshot, setDoc, updateDoc } from "firebase/firestore";
import React, { useEffect, useState, useContext } from "react";
import { settingsRef } from "../firebase";
import { AuthContext } from "../providers/AuthProvider";
// import { UserContext } from "./UserProvider";

const MS_IN_ONE_DAY = 86400000;
const DEFAULT_DAYS_NUMBER = 365;

export const SettingsContext = React.createContext({
  currentTsNumber: 1,
  currentQuoteNumber: 1,
  currentJobsheetNumber: 1,
  currentRateNumber: 1,
  currentMaterialsPercentage: 20,
  listenForDays: DEFAULT_DAYS_NUMBER,
  businessInfo: [],
  vat: false,
  aiThreadMatchingEnabled: true,
  draftPollingEnabled: true,
});

// 30 days in ms: 2592000000
// 60 days in ms: 5184000000
// 90 days in ms: 7776000000
// 1 year in ms : 31536000000

const SettingsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [tsNumber, setTsNumber] = useState(null);
  const [quoteNumber, setQuoteNumber] = useState(null);
  const [jobsheetNumber, setJobsheetNumber] = useState(null);
  const [rateNumber, setRateNumber] = useState(null);
  const [materialsPercentage, setMaterialsPercentage] = useState(null);

  // CHange vat to default false when we go vat deregistered. And true whn vat registered
  const [vat, setVat] = useState(true);

  const [aiThreadMatchingEnabled, setAiThreadMatchingEnabled] = useState(true);

  // Lets Sent/Paid/Cert auto-detection (via Gmail draft polling - see
  // useBatchPendingDraftsCheck and PrimaryForm) be switched off entirely without a deploy,
  // e.g. if it starts misbehaving in production. Defaults to on.
  const [draftPollingEnabled, setDraftPollingEnabled] = useState(true);

  // Don't need to store a listenForDays variable in the database because it will be different for each
  // computer logged into the same account. So just store in local storage.

  // ---------------------------------------------------------------------------------------------------------------
  // TODO **** Up for deletion:

  // const [listenForDays, setDaysNumber] = useState(null);
  // const [daysInMs, setDaysInMs] = useState(listenForDays * MS_IN_ONE_DAY); // Days in units of milliseconds;

  // const [startDate, setStartDate] = useState(
  //   () => Date.now() - listenForDays * MS_IN_ONE_DAY
  // );

  // useEffect(() => {
  //   setDaysInMs(listenForDays * MS_IN_ONE_DAY);
  //   setStartDate(Date.now() - daysInMs);
  // }, [listenForDays, daysInMs]);

  // const changeDaysNumber = async function (newNumber) {
  // setDoc(
  //   userSettingsRef,
  //   { listenForDays: Number(newNumber) },
  //   { merge: true }
  // );
  // };
  // ---------------------------------------------------------------------------------------------------------------

  // Listen for days logic >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

  // Associated state
  const [days, setDays] = useState(localStorage.getItem("days") || DEFAULT_DAYS_NUMBER);
  const [startDate, setStartDate] = useState(Date.now() - Number(days) * MS_IN_ONE_DAY);

  // Associated functions
  const changeDaysNumber = (newNumber) => {
    // Change number of days in settings, update relevant state and local storage

    if (!Number(newNumber)) {
      console.error("Days needs to be a number", newNumber);
      return;
    }

    setDays(Number(newNumber));
    setStartDate(Date.now() - Number(newNumber) * MS_IN_ONE_DAY);
    localStorage.setItem("days", String(newNumber));
  };

  // Listen for days logic <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  const [businessInfo, setBusinessInfo] = useState([]);

  let userUid = user?.uid;
  let userSettingsRef = userUid ? doc(settingsRef, userUid) : null;

  useEffect(() => {
    if (!user?.uid) {
      return;
    }

    const unsubFromSettings = onSnapshot(userSettingsRef, (doc) => {
      if (!doc.exists()) {
        setDoc(userSettingsRef, {
          currentTsNumber: 1,
          currentQuoteNumber: 1,
          currentJobsheetNumber: 1,
          currentRateNumber: 1,
          currentMaterialsPercentage: 20,
          // listenForDays: 100,
          businessInfo: [],
          vat: false,
          aiThreadMatchingEnabled: true,
          draftPollingEnabled: true,
        });
      } else {
        if (doc.data().currentTsNumber) {
          setTsNumber(doc.data().currentTsNumber);
        }
        if (doc.data().currentQuoteNumber) {
          setQuoteNumber(doc.data().currentQuoteNumber);
        }
        if (doc.data().currentJobsheetNumber) {
          setJobsheetNumber(doc.data().currentJobsheetNumber);
        }
        if (doc.data().currentRateNumber) {
          setRateNumber(doc.data().currentRateNumber);
        }
        // if (doc.data().listenForDays) {
        //   setDaysNumber(doc.data().listenForDays);
        // }
        if (doc.data().businessInfo) {
          setBusinessInfo(doc.data().businessInfo);
        }
        if (doc.data().currentMaterialsPercentage || doc.data().currentMaterialsPercentage === 0) {
          setMaterialsPercentage(doc.data().currentMaterialsPercentage);
        }
        if (!doc.data().currentMaterialsPercentage) {
          setMaterialsPercentage(0);
        }
        if (typeof doc.data().vat === "boolean") {
          setVat(doc.data().vat);
        }
        if (typeof doc.data().aiThreadMatchingEnabled === "boolean") {
          setAiThreadMatchingEnabled(doc.data().aiThreadMatchingEnabled);
        }
        if (typeof doc.data().draftPollingEnabled === "boolean") {
          setDraftPollingEnabled(doc.data().draftPollingEnabled);
        }
      }
    });

    return () => {
      unsubFromSettings();
    };
  }, [user, userUid]);

  const addBusinessInfo = async function (infoData) {
    return updateDoc(userSettingsRef, {
      businessInfo: arrayUnion(infoData),
    });
  };

  const removeBusinessInfo = async function (infoUid) {
    let newBusinessInfo = businessInfo.filter((bInfo) => bInfo.uid !== infoUid);

    updateDoc(userSettingsRef, {
      businessInfo: newBusinessInfo,
    });
  };

  const alterBusinessInfo = async function (infoUid, name, value) {
    let alteredBusinessInfo = businessInfo.map((bInfo) => {
      if (infoUid === bInfo.uid) {
        return { ...bInfo, [name]: value };
      } else {
        return bInfo;
      }
    });

    updateDoc(userSettingsRef, {
      businessInfo: alteredBusinessInfo,
    });
  };

  const changeMaterialsPercentage = async function (newNumber) {
    setDoc(userSettingsRef, { currentMaterialsPercentage: Number(newNumber) }, { merge: true });
  };

  const changePrimaryNumber = async function (type = "", newNumber) {
    let key = undefined;

    if (type === "timesheet") {
      key = "currentTsNumber";
    }

    if (type === "quote") {
      key = "currentQuoteNumber";
    }

    if (type === "jobsheet") {
      key = "currentJobsheetNumber";
    }

    if (type === "rate") {
      key = "currentRateNumber";
    }

    if (key) {
      setDoc(userSettingsRef, { [key]: newNumber }, { merge: true });
    }
  };

  const incCurrentPrimaryNumber = async function (type = "") {
    let key = undefined;

    if (type === "timesheet") {
      key = "currentTsNumber";
    }

    if (type === "quote") {
      key = "currentQuoteNumber";
    }

    if (type === "jobsheet") {
      key = "currentJobsheetNumber";
    }

    if (type === "rate") {
      key = "currentRateNumber";
    }

    if (key) {
      setDoc(userSettingsRef, { [key]: increment(1) }, { merge: true });
    }
  };

  const toggleVat = async (currentValue) => {
    let newValue = !currentValue;
    setDoc(userSettingsRef, { vat: newValue }, { merge: true });
  };

  const toggleAiThreadMatching = async (currentValue) => {
    let newValue = !currentValue;
    setDoc(userSettingsRef, { aiThreadMatchingEnabled: newValue }, { merge: true });
  };

  const toggleDraftPolling = async (currentValue) => {
    let newValue = !currentValue;
    setDoc(userSettingsRef, { draftPollingEnabled: newValue }, { merge: true });
  };

  return (
    <SettingsContext.Provider
      value={{
        currentTsNumber: tsNumber,
        currentRateNumber: rateNumber,
        currentJobsheetNumber: jobsheetNumber,
        incCurrentPrimaryNumber,
        currentQuoteNumber: quoteNumber,
        changePrimaryNumber,
        currentMaterialsPercentage: materialsPercentage,
        changeMaterialsPercentage,
        businessInfo,
        addBusinessInfo,
        alterBusinessInfo,
        removeBusinessInfo,
        listenForDays: days, // integer,
        changeDaysNumber, // fn
        startDate, // milliseconds
        vat,
        toggleVat, // fn
        aiThreadMatchingEnabled,
        toggleAiThreadMatching, // fn
        draftPollingEnabled,
        toggleDraftPolling, // fn
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsProvider;
